import React, { useEffect, useState } from 'react';
import connect from 'Shared/connect';
import { styled } from '@glitz/react';
import { get, URLX } from '@polarnopyret/scope';
import VariationStoreStockViewModel from 'Product/VariationStoreStockViewModel.type';
import StoreAvailabilityCard from './StoreAvailabilityCard';
import StoreModel from 'Stores/StoreModel.type';
import { SetSelectedStore } from 'Shared/uiReducer';
import StoreAvailabilityViewModelLabels from 'Product/VariationViewModel+StoreAvailabilityViewModelLabels.type';
import SearchStores from './SearchStores';
import { loadScript } from '@polarnopyret/scope';
import { filterByDistance, sortByCrowFliesDistance } from 'Shared/distance-calculations';
import { formatStringByKeys } from 'Shared/utils';
import { getCookie, setCookie } from 'Shared/Helper/cookieHelper';
import Modal, { ModalScrollableContent, ModalTopContent } from 'Shared/SharedComponents/atoms/Modal';
import { Paragraph, ResponsiveSize } from 'Shared/SharedComponents/atoms/Typography';
import { isCompact } from 'Shared/Viewport';

const AVAILABILITY_STATUS_OUT_OF_STOCK = 'RED';
const MAX_DISTANCE = 5000;
const COOKIE_POP_SELECTED_STORE = 'pop.selected-store';

const ModalScrollableContentContainer = styled(ModalScrollableContent, {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  gap: '12px',
  paddingTop: '12px'
})

const ModalIntro = styled(Paragraph, {
  marginTop: '8px'
})

type RequiredPropType = {
  modalIsShown: boolean;
  hideModal: () => void;
  labels: StoreAvailabilityViewModelLabels;
};

type ConnectedPropType = {
  currentBreakpoint: number;
  stores: { [item: string]: StoreModel };
  setSelectedStore: (selectedStore: StoreModel) => void;
};

type PropType = RequiredPropType &
  ConnectedPropType & {
    storeAvailability?: VariationStoreStockViewModel;
    googleMapsApiKey: string;
  };

interface StoreWithId extends StoreModel {
  id: string;
}

const StoreAvailabilityModal = ({
  modalIsShown,
  hideModal,
  labels,
  stores,
  setSelectedStore,
  googleMapsApiKey,
  currentBreakpoint,
}: PropType) => {

  const [storeAvailability, setStoreAvailability] = useState<undefined | VariationStoreStockViewModel>(undefined);
  const [onlyShowIfInStock, setOnlyShowIfInStock] = useState<boolean>(true);
  const [isPositionChecked, setIsPositionChecked] = useState<boolean>(false);
  const [positionAllowedByClient, setPositionAllowedByClient] = useState<boolean>(false);
  const [selectedStoreId, setSelectedStoreId] = useState<string>('');
  const storeAvailabilityInStock = (storeAvailability?.stores?.findIndex(x => x.availabilityStatus !== AVAILABILITY_STATUS_OUT_OF_STOCK) ?? -1) > -1;
  const [storesLoaded, setStoresLoaded] = useState<boolean>(false);
  const [showSpinner, setShowSpinner] = useState<boolean>(true);

  const isMobile = isCompact(currentBreakpoint);

  async function collectStoreData(stores: StoreWithId[]) {
    const storeIds = stores.reduce((ids: string, store: StoreWithId) => {
      return `${ids},${store.id}`;
    }, '');
    const url = new URLX(document.URL);
    url.search = `?stores=${storeIds}`;
    return get(url).then((r) => r.json());
  }

  function searchForStores(searchString: string) {
    const url = new URLX(document.URL);
    url.search = `?storeSearch=${searchString}`;
    setStoreAvailability(null);
    setStoresLoaded(false);

    if (searchString === '') {
      getStores();
      return;
    }

    get(url)
      .then((r) => r.json())
      .then((json: VariationStoreStockViewModel) => {
        setStoreAvailability(json);
        setStoresLoaded(true);
      });
  }

  const storesWithIds: StoreWithId[] = [];
  for (const key in stores) {
    if (stores.hasOwnProperty(key)) {
      storesWithIds.push({ ...stores[key], id: key });
    }
  }

  function getStores() {
    navigator.geolocation?.getCurrentPosition(
      (position) => {
        loadScript(
          'https://maps.googleapis.com/maps/api/js' + (googleMapsApiKey ? '?key=' + googleMapsApiKey : ''),
        ).then(() => {

          filterByDistance(storesWithIds, position, MAX_DISTANCE).then((filteredStores: StoreWithId[]) => {
            collectStoreData(filteredStores).then((storeAvailabilityJson) => {
              if (storeAvailabilityJson?.stores) {
                storeAvailabilityJson.stores = sortByCrowFliesDistance(storeAvailabilityJson.stores, position);
              }
              setStoreAvailability(storeAvailabilityJson);
              setStoresLoaded(true);
              setShowSpinner(false);
            });
            setIsPositionChecked(true);
            setPositionAllowedByClient(true);
          });
        });
      },
      () => {
        // Could not get the client location; must collect all store data and cannot sort based on position
        collectStoreData(storesWithIds).then((storeAvailabilityJson) => {
          setStoreAvailability(storeAvailabilityJson);
          setStoresLoaded(true);
          setShowSpinner(false);
        });
        setIsPositionChecked(true);
        setPositionAllowedByClient(false);
      },
    );
  }

  if (!isPositionChecked && modalIsShown) {
    getStores();
  }

  useEffect(() => {
    if (selectedStoreId === '') {
      const cookieStoreId = getCookie(COOKIE_POP_SELECTED_STORE);
      if (cookieStoreId) {
        setSelectedStoreId(cookieStoreId);
      }
    } else {
      setSelectedStore(stores[selectedStoreId]);
      if (selectedStoreId !== getCookie(COOKIE_POP_SELECTED_STORE)) {
        setCookie(COOKIE_POP_SELECTED_STORE, selectedStoreId);
      }
    }
  }, [selectedStoreId]);

  return (
    <Modal modalIsShown={modalIsShown} hideModal={hideModal} widthInColumns={6}>
      <ModalTopContent
        textAboveHeading={labels.label}
        heading={labels.heading}
        closeModal={hideModal}
        textAboveLine={positionAllowedByClient ?
          storeAvailability?.stores
            ? formatStringByKeys(labels.resultsNear, {
              count: storeAvailability.stores.length.toString(),
              distance: MAX_DISTANCE.toString(),
            })
            : ' ' : (storeAvailability?.stores ? `${storeAvailability.stores.length.toString()} ${labels.results}` : ' ')
        }
      >
        {labels.intro?.length > 0 && (
          <ModalIntro fontSize={ResponsiveSize.D14_M14_L130}>{labels.intro}</ModalIntro>
        )}
        <SearchStores
          labels={{ searchPlaceholder: labels.searchPlaceHolder, searchInStockToggle: labels.searchInStockToggle }}
          updateResults={searchForStores}
          setOnlyShowIfInStock={setOnlyShowIfInStock}
          isCompact={isMobile}
          showSpinner={showSpinner}
        />
      </ModalTopContent>
      <ModalScrollableContentContainer>
        {storeAvailability &&
          storeAvailability?.stores?.map((store, index) => {
            if (!onlyShowIfInStock || store.availabilityStatus !== AVAILABILITY_STATUS_OUT_OF_STOCK)
              return (
                <StoreAvailabilityCard
                  key={index}
                  isSelected={store.id === selectedStoreId}
                  storeAvailability={store}
                  labels={labels}
                  isCompact={isMobile}
                  onClick={() => setSelectedStoreId(store.id)}
                />
              );
          })}
        {!storeAvailabilityInStock && onlyShowIfInStock && storesLoaded && (
          <div>
            {labels.noStoresInStock}
          </div>)
        }
      </ModalScrollableContentContainer>
    </Modal>
  );
};

export default connect(
  (state) => ({
    stores: state.appShellData.store.stores,
    selectedStore: state.UI?.selectedStore,
    googleMapsApiKey: state.appShellData.apiKeys.googleMapsApiKey,
    currentBreakpoint: state.currentBreakpoint,
  }),
  (dispatch) => ({
    setSelectedStore: (selectedStore: StoreModel) => dispatch(SetSelectedStore(selectedStore)),
  }),
)(StoreAvailabilityModal);
